import { Temporal } from "@js-temporal/polyfill";
import { useEffect, useMemo, useRef, type ReactNode } from "react";
import { DropletIcon, MistIcon, PinIcon, RainIcon, SunIcon } from "@/app/components/system/Icons";
import { WeatherGlyph } from "@/app/components/weather/WeatherGlyph";
import { useWeatherBackdrop } from "@/app/components/weather/WeatherBanner";
import { LocationPill } from "@/app/components/weather/LocationPill";
import { TempBar, computeTempScale } from "@/app/components/weather/TempBar";
import { useWeather } from "@/app/lib/data-context";
import { COND_LABELS } from "@/app/lib/weather";
import { formatSunHours } from "@/lib/sun-hours";
import type { DayForecast, WeatherCondition, WeatherForCurrent5DaysWindow } from "@/types";

interface MetricProps {
  icon: ReactNode;
  label: string;
  value: string;
}

function Metric({ icon, label, value, illustratedDark = false }: MetricProps & { illustratedDark?: boolean }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className={`grid place-items-center ${illustratedDark ? "text-white/70" : "text-ink-2"}`}>{icon}</span>
      <span className="inline-flex flex-col items-start gap-0.5 leading-none">
        <span className={`font-sans text-[9.5px] font-semibold uppercase tracking-[0.12em] ${illustratedDark ? "text-white/45" : "text-ink-3"}`}>
          {label}
        </span>
        <span className={`font-sans text-[13px] font-medium ${illustratedDark ? "text-white/75" : "text-ink-2"}`}>{value}</span>
      </span>
    </span>
  );
}

function formatRainMm(mm: number) {
  if (mm < 0.05) return "0 mm";
  if (mm < 1) return `${mm.toFixed(1)} mm`;
  return `${Math.round(mm)} mm`;
}

export function summarizeDay(d: DayForecast) {
  const daytime = d.hours.filter((h) => h.isDay);
  const sample = daytime.length > 0 ? daytime : d.hours;
  const temps = sample.map((h) => h.tempC);
  const precipitationMm = d.hours.reduce((sum, h) => sum + h.precipitationMm, 0);
  const sunHours = d.hours.reduce(
    (sum, h) => sum + h.sunshine.q1 + h.sunshine.q2 + h.sunshine.q3 + h.sunshine.q4,
    0,
  ) / 3600;
  return {
    date: d.date,
    label: new Date(`${d.date}T00:00:00`).toLocaleDateString("en-US", { weekday: "long" }).toUpperCase(),
    condition: d.condition,
    tempMaxC: Math.max(...temps),
    tempMinC: Math.min(...d.hours.map((h) => h.tempC)),
    precipitationMm,
    sunHours,
  };
}

export function WeatherHero({ showDayOutlook = true, illustratedDark = false }: Readonly<{ showDayOutlook?: boolean; illustratedDark?: boolean }>) {
  const { weather, location } = useWeather();
  const { dark: backdropDark } = useWeatherBackdrop();
  const dark = illustratedDark || backdropDark;

  if (!location) {
    return (
      <section className={`relative flex flex-col items-center text-center gap-2 pt-2 pb-6 px-4 max-w-90 mx-auto ${illustratedDark ? "text-paper" : "text-ink"}`}>
        <span
          className={`w-12 h-12 rounded-full grid place-items-center mb-1 border ${illustratedDark ? "text-paper" : "text-ink"}`}
          style={{
            background: illustratedDark ? "oklch(1 0 0 / 0.14)" : "oklch(1 0 0 / 0.78)",
            borderColor: illustratedDark ? "oklch(1 0 0 / 0.22)" : "oklch(1 0 0 / 0.55)",
          }}
        >
          <PinIcon size={22} stroke={1.6} />
        </span>
        <h2 className="m-0 font-display font-medium text-[22px] tracking-tight">Where is your garden?</h2>
        <p className={`mt-0 mb-1.5 max-w-[32ch] font-sans text-[13.5px] leading-[1.45] ${illustratedDark ? "text-white/70" : "text-ink-2"}`}>
          Set a location and we'll match watering reminders to local rain, heat, and frost.
        </p>
        <LocationPill dark={dark} />
      </section>
    );
  }

  if (!weather) return <WeatherHeroLoading illustratedDark={illustratedDark} />;

  const currentHourIndex = Temporal.Now.zonedDateTimeISO().hour;
  const currentHour = weather.today.hours[currentHourIndex];
  if (!currentHour) return <WeatherHeroLoading illustratedDark={illustratedDark} />;
  const cond = currentHour.condition;

  return <WeatherHeroLoaded
    weather={weather}
    currentHour={currentHour}
    currentHourIndex={currentHourIndex}
    cond={cond}
    showDayOutlook={showDayOutlook}
    illustratedDark={illustratedDark}
  />;
}

interface LoadedProps {
  weather: WeatherForCurrent5DaysWindow;
  currentHour: DayForecast["hours"][number];
  currentHourIndex: number;
  cond: WeatherCondition;
  showDayOutlook: boolean;
  illustratedDark: boolean;
}

function WeatherHeroLoaded({ weather, currentHour, currentHourIndex, cond, showDayOutlook, illustratedDark }: LoadedProps) {
  const today = weather.today;

  const hours = useMemo(
    () => [
      ...today.hours.map((h, i) => ({ ...h, dayOffset: 0, idx: i })),
      ...weather.oneDayLater.hours.map((h, i) => ({ ...h, dayOffset: 1, idx: i })),
    ],
    [today, weather.oneDayLater],
  );

  const days = [weather.oneDayLater, weather.twoDaysLater].map((d, i) => {
    const summary = summarizeDay(d);
    summary.label = i === 0 ? "TMRW" : summary.label.slice(0, 3);
    return summary;
  });

  const { globalMin, globalMax } = computeTempScale([summarizeDay(today), ...days]);

  const precipitationMmToday = today.hours.reduce((sum, h) => sum + h.precipitationMm, 0);
  const sunHoursToday = today.hours.reduce(
    (sum, h) => sum + h.sunshine.q1 + h.sunshine.q2 + h.sunshine.q3 + h.sunshine.q4,
    0,
  ) / 3600;

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const currentCardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = scrollRef.current;
    const card = currentCardRef.current;
    if (!container || !card) return;
    container.scrollLeft = Math.max(0, card.offsetLeft - 22);
  }, []);

  const dividerColor = illustratedDark ? "rgba(255,255,255,0.12)" : "oklch(0.2 0.02 140 / 0.18)";

  return (
    <section
      className={`relative ${illustratedDark ? "text-paper" : "text-ink"}`}
      style={{ animation: "wh-hero-in 360ms ease-out both" }}
    >
      <div
        ref={scrollRef}
        className="relative flex items-stretch overflow-x-auto [scroll-snap-type:x_proximity] [-webkit-overflow-scrolling:touch] [&::-webkit-scrollbar]:hidden -mx-5.5 px-5.5 mb-3"
      >
        {hours.map((h) => {
          const isCurrent = h.dayOffset === 0 && h.idx === currentHourIndex;
          if (isCurrent) {
            return (
              <div
                key={`now-${h.idx}`}
                ref={currentCardRef}
                className="flex items-start gap-2.5 flex-none snap-start py-1 px-4"
              >
                <span className={`font-display font-medium text-[60px] leading-[0.92] tracking-[-0.04em] ${illustratedDark ? "text-paper" : "text-ink"}`}>
                  {Math.round(h.tempC)}°
                </span>
                <div className={`flex flex-col items-start gap-1 mt-1.5 whitespace-nowrap ${illustratedDark ? "text-white/70" : "text-ink-2"}`}>
                  <span className="font-sans text-[10.5px] font-semibold uppercase tracking-[0.12em]">Now</span>
                  <span className="grid place-items-center" aria-label={COND_LABELS[cond]}>
                    <WeatherGlyph condition={cond} size={32} />
                  </span>
                </div>
              </div>
            );
          }
          return (
            <div
              key={`${h.dayOffset}-${h.idx}`}
              className={`mx-1 flex flex-col items-center gap-1 py-1.5 px-2 rounded-[10px] min-w-12 flex-none snap-start backdrop-blur-xs border ${illustratedDark ? "bg-white/10 border-white/15" : "bg-white/30 border-transparent"}`}
            >
              <span className={`font-sans text-[10.5px] font-medium uppercase tracking-[0.04em] ${illustratedDark ? "text-white/50" : "text-ink-2"}`}>
                {h.label}
              </span>
              <span className="grid place-items-center h-5.5"><WeatherGlyph condition={h.condition} size={18} /></span>
              <span className={`font-display font-semibold text-[13px] tracking-tight ${illustratedDark ? "text-paper" : "text-ink"}`}>
                {Math.round(h.tempC)}°
              </span>
            </div>
          );
        })}
      </div>

      <div className={`grid grid-cols-4 gap-x-3 gap-y-2 font-sans pt-2 transition-[margin] duration-300 ease-out motion-reduce:transition-none ${showDayOutlook ? "mb-4" : "mb-0"} ${illustratedDark ? "text-white/75" : "text-ink-2"}`}>
        <Metric icon={<DropletIcon size={20} stroke={1.6} />} label="Humidity" value={`${currentHour.humidity}%`} illustratedDark={illustratedDark} />
        <Metric icon={<MistIcon size={20} stroke={1.6} />} label="Wind" value={`${Math.round(currentHour.windKmh)} km/h`} illustratedDark={illustratedDark} />
        <Metric icon={<RainIcon size={20} stroke={1.6} />} label="Rain" value={formatRainMm(precipitationMmToday)} illustratedDark={illustratedDark} />
        <Metric icon={<SunIcon size={20} stroke={1.6} />} label="Sun" value={formatSunHours(sunHoursToday)} illustratedDark={illustratedDark} />
      </div>

      <WeatherDayOutlook
        days={days}
        globalMin={globalMin}
        globalMax={globalMax}
        dividerColor={dividerColor}
        showDayOutlook={showDayOutlook}
        illustratedDark={illustratedDark}
      />
    </section>
  );
}

interface DayOutlookSummary {
  date: string;
  label: string;
  condition: WeatherCondition;
  tempMinC: number;
  tempMaxC: number;
  precipitationMm: number;
  sunHours: number;
}

/** Tomorrow + day-after rows; collapsible on Library scroll to free space for pots. */
function WeatherDayOutlook({ days, globalMin, globalMax, dividerColor, showDayOutlook, illustratedDark = false }: Readonly<{
  days: DayOutlookSummary[];
  globalMin: number;
  globalMax: number;
  dividerColor: string;
  showDayOutlook: boolean;
  illustratedDark?: boolean;
}>) {
  return (
    <div
      className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none ${showDayOutlook ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
      aria-hidden={!showDayOutlook}
    >
      <div className="overflow-hidden min-h-0">
        <div
          className="flex flex-col font-sans border-t pt-2"
          style={{ borderColor: dividerColor }}
        >
          {days.map((d) => (
            <div key={d.date} className="flex items-center gap-2 py-1.5">
              <span className={`uppercase tracking-widest text-[10.5px] font-medium min-w-10 ${illustratedDark ? "text-white/50" : "text-ink-2"}`}>
                {d.label}
              </span>
              <span className="grid place-items-center">
                <WeatherGlyph condition={d.condition} size={18} />
              </span>
              <span className={`font-display font-semibold text-[12px] tracking-tight w-7 text-right ${illustratedDark ? "text-white/45" : "text-ink-3"}`}>
                {Math.round(d.tempMinC)}°
              </span>
              <TempBar
                dayMin={d.tempMinC}
                dayMax={d.tempMaxC}
                globalMin={globalMin}
                globalMax={globalMax}
              />
              <span className={`font-display font-semibold text-[13px] tracking-tight w-7 text-left ${illustratedDark ? "text-paper" : "text-ink"}`}>
                {Math.round(d.tempMaxC)}°
              </span>
              <span className={`inline-flex items-center gap-2 text-[11px] ${illustratedDark ? "text-white/50" : "text-ink-2"}`}>
                <span className="inline-flex items-center gap-1">
                  <RainIcon size={13} stroke={1.6} />
                  {formatRainMm(d.precipitationMm)}
                </span>
                <span className="inline-flex items-center gap-1">
                  <SunIcon size={13} stroke={1.6} />
                  {formatSunHours(d.sunHours)}
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function WeatherHeroLoading({ illustratedDark = false }: Readonly<{ illustratedDark?: boolean }>) {
  return (
    <section
      role="status"
      aria-live="polite"
      className={`relative flex flex-col items-center text-center gap-2 pt-2 pb-6 px-4 max-w-90 mx-auto ${illustratedDark ? "text-paper" : "text-ink"}`}
      style={{ animation: "wh-hero-in 360ms ease-out both" }}
    >
      <span
        className={`w-12 h-12 rounded-full grid place-items-center mb-1 border ${illustratedDark ? "text-paper" : "text-ink"}`}
        style={{
          background: illustratedDark ? "oklch(1 0 0 / 0.14)" : "oklch(1 0 0 / 0.78)",
          borderColor: illustratedDark ? "oklch(1 0 0 / 0.22)" : "oklch(1 0 0 / 0.55)",
          animation: "scan-overlay-pulse 1.4s ease-in-out infinite",
        }}
      >
        <WeatherGlyph condition="cloud" size={28} />
      </span>
      <p className={`m-0 font-display italic text-[15px] ${illustratedDark ? "text-white/70" : "text-ink-2"}`}>Reading the sky…</p>
      <span className="sr-only">Loading weather forecast</span>
    </section>
  );
}
